import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Button from '../button/button'
import Logo from '../../../images/icon512-gray.png'
import UserService from '../../services/user'

const HomeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 1;
  background-color: #90d1a8;
  // background-color: #333;
  text-align: center;
`

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 12px;
  color: #333;
  font-weight: bold;
  margin-block-start: 32.16px;
  margin-block-end: 12px;
  // color: #90d1a8;
`

const StatsStrip = styled.div`
  width: 100%;
  max-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 8px;
`

const StatLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background-color: rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  border: 1px solid #7da78f;
  padding: 5px 8px;
  color: #2f4c3d;
  font-size: 11px;
  font-weight: 600;
`

const StatValue = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #1f3228;
`

const StatHint = styled.span`
  color: #3f5f4f;
  font-size: 11px;
  font-weight: 500;
`

const ProgressCopy = styled.div`
  margin-bottom: 18px;
  font-size: 12px;
  color: #395547;
`

const ButtonBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`

// const StyledButton = styled(Button)`
//   width: 200px;
//   padding: 10px;
//   font-size: 1.2rem;
//   background-color: #90d1a8;
//   // color: white;
//   color: #333;
//   border-color: #333;
//   // border: 1px solid #333;
//   text-decoration: none;
//   display: inline-flex;
//   justify-content: center;
//   align-items: center;
//   border-radius: 5px;
//   cursor: pointer;
//   transition: background-color 0.3s;

//   // &:hover {
//   //   background-color: #78b08f;
//   // }
// `

const HOME_PROFILE_CACHE_KEY = 'wordgrid.home.profile'

const getTodayUtcDate = () => new Date().toISOString().slice(0, 10)

const getUtcCutoffLocalLabel = () => {
  const now = new Date()
  const nextUtcMidnight = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0,
    0,
    0,
    0,
  ))
  const parts = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(nextUtcMidnight)
  const hour = parts.find((part) => part.type === 'hour')?.value || ''
  const minute = parts.find((part) => part.type === 'minute')?.value || '00'
  const dayPeriod = (parts.find((part) => part.type === 'dayPeriod')?.value || '').toLowerCase()
  const minuteSuffix = minute === '00' ? '' : `:${minute}`
  return `${hour}${minuteSuffix}${dayPeriod}`
}

const Home = () => {
  const title = 'WordGrid.io'
  const [profile, setProfile] = useState(null)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)

  useEffect(() => {
    let isMounted = true
    const loadProfile = async () => {
      setIsLoadingProfile(true)
      try {
        const userToken = localStorage.getItem('userToken')
        if (!userToken) {
          return
        }
        const userProfile = await UserService.getProfile(userToken)
        if (!isMounted) {
          return
        }
        setProfile(userProfile)
        localStorage.setItem(HOME_PROFILE_CACHE_KEY, JSON.stringify(userProfile))
      } catch (error) {
        console.warn('Failed to load profile for home screen', error)
        try {
          const cachedRaw = localStorage.getItem(HOME_PROFILE_CACHE_KEY)
          if (cachedRaw && isMounted) {
            setProfile(JSON.parse(cachedRaw))
          }
        } catch (cacheError) {
          console.warn('Failed to load cached home profile', cacheError)
        }
      } finally {
        if (isMounted) {
          setIsLoadingProfile(false)
        }
      }
    }

    loadProfile()
    return () => {
      isMounted = false
    }
  }, [])

  const streakCopy = useMemo(() => {
    if (isLoadingProfile) {
      return 'Loading your progress...'
    }
    const todayUtcDate = getTodayUtcDate()
    const lastCompletedOn = typeof profile?.streakLastCompletedOn === 'string'
      ? profile.streakLastCompletedOn.slice(0, 10)
      : null
    return lastCompletedOn === todayUtcDate
      ? `Streak secured for today. Next reset at ${getUtcCutoffLocalLabel()}.`
      : `Complete 1 puzzle before ${getUtcCutoffLocalLabel()} to keep your streak alive.`
  }, [isLoadingProfile, profile?.streakLastCompletedOn])

  return (
    <HomeContainer>
      <img width={64} src={Logo} />  
      <Title>{title}</Title>
      <StatsStrip>
        <StatLine>
          <span role='img' aria-label='streak'>🔥</span>
          <span>Streak</span>
          <StatValue>{isLoadingProfile ? '...' : String(profile?.dailyStreak || 0)}</StatValue>
          <StatHint>days</StatHint>
        </StatLine>
        <StatLine>
          <span role='img' aria-label='completed'>✅</span>
          <span>Completed</span>
          <StatValue>{isLoadingProfile ? '...' : String(profile?.completedGames || 0)}</StatValue>
          <StatHint>total</StatHint>
        </StatLine>
      </StatsStrip>
      <ProgressCopy>{streakCopy}</ProgressCopy>
      <ButtonBox>
        <Button text='New Game' to='/game/new' />
        <Button text='Continue' to='/game/continue' />
        <Button text='Settings' to='/settings' />
        <Button text='Feedback' to='/feedback' />
      </ButtonBox>
    </HomeContainer>
  )
}

export default Home
